import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import TaskKanbanBoard from '../../../components/work/tasks/TaskKanbanBoard';
import TasksTable from '../../../components/work/tasks/TasksTable';
import { Button, Dropdown, Modal, Segmented, Select, Space, message } from 'antd';
import {
  PlusOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
  AppstoreAddOutlined,
  FileExcelOutlined,
  DownOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { tasksApi } from '../../../utils/workApi';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';
import { useAuth } from '../../../contexts/AuthContext';
import { readCachedAuthUser, readCachedAuthUserId } from '../../../utils/cachedAuthUser';
import {
  toFilterSelectId,
  mapFilterSelectOptions,
  ensureFilterSelectOption,
} from '../../../utils/filterDateUtils';
import { loadTasksListState, saveTasksListState } from '../../../utils/tasksListState';
import { buildWorkListFilters, getDefaultWorkDateRange, createWorkDateRangeFilter, applyWorkDateRangeChange } from '../../../utils/workFilterUtils';
import { buildCommonDatePresets } from '../../../utils/filterDateUtils';
import {
  BULK_ACTION_BAR_CLASS,
  BULK_ACTION_BAR_TEXT_CLASS,
  PANEL_PRIMARY_BTN_CLASS,
} from '../../../constants/ui';

// پرچم فعال‌سازی لاگ بررسی مجوزها در کنسول مرورگر
const DEBUG_TASK_PERMISSIONS = true;

const normalizeTaskFilterIds = (filters) => {
  if (!filters || typeof filters !== 'object') return filters;
  return {
    ...filters,
    project_id: toFilterSelectId(filters.project_id),
    client_id: toFilterSelectId(filters.client_id),
    assigned_to: toFilterSelectId(filters.assigned_to),
    assigned_by: toFilterSelectId(filters.assigned_by),
    category_id: toFilterSelectId(filters.category_id),
    label_id: toFilterSelectId(filters.label_id),
    milestone_id: toFilterSelectId(filters.milestone_id),
  };
};

const buildDefaultTaskFilters = (userId = null, dateRange = null) => {
  const range = dateRange || getDefaultWorkDateRange();
  const cached = userId ?? readCachedAuthUserId();
  return {
    startDate: range.startDate,
    endDate: range.endDate,
    project_id: 'all',
    client_id: 'all',
    assigned_to: cached != null && cached !== 'all' ? toFilterSelectId(cached) : 'all',
    assigned_by: 'all',
    priority: 'all',
    category_id: 'all',
    label_id: 'all',
    status: 'all',
    billable: 'all',
    milestone_id: 'all',
    type: 'all',
    pinned: 'all',
  };
};

const buildInitialFilterLabels = (assignedToId, storedLabels = {}, cachedUser = null) => {
  const labels = { ...(storedLabels || {}) };
  if (assignedToId == null || assignedToId === 'all') return labels;
  if (labels.assigned_to) return labels;
  const user = cachedUser || readCachedAuthUser();
  if (user?.name && user?.id != null && Number(user.id) === Number(assignedToId)) {
    labels.assigned_to = user.name;
  }
  return labels;
};

const buildInitialTasksState = (storedListState, defaultRange) => {
  const cachedUser = readCachedAuthUser();
  const cachedId = cachedUser?.id ?? readCachedAuthUserId();
  const defaults = buildDefaultTaskFilters(cachedId, defaultRange);
  const assignedToAll = storedListState?.assignedToAll === true;

  let filters = defaults;
  if (storedListState?.filters) {
    filters = normalizeTaskFilterIds({ ...defaults, ...storedListState.filters });
    if (filters.assigned_to === 'all' && !assignedToAll) {
      filters = { ...filters, assigned_to: defaults.assigned_to };
    }
  }

  const filterLabels = buildInitialFilterLabels(
    filters.assigned_to,
    storedListState?.filterLabels,
    cachedUser,
  );

  return {
    filters,
    filterLabels,
    assignedToAll: assignedToAll && filters.assigned_to === 'all',
    viewMode: storedListState?.viewMode || 'kanban',
    searchText: storedListState?.searchText ?? '',
    pagination: {
      current: storedListState?.pagination?.current || 1,
      pageSize: storedListState?.pagination?.pageSize || 10,
    },
  };
};

const Tasks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAdd, canEditRecord, canDeleteRecord, permissions: taskPermissions } =
    useResourcePermissions('tasks');
  const defaultRange = useMemo(() => getDefaultWorkDateRange(), []);
  const storedListState = useMemo(() => loadTasksListState(), []);
  const initialTasksState = useMemo(
    () => buildInitialTasksState(storedListState, defaultRange),
    [storedListState, defaultRange],
  );

  useEffect(() => {
    if (DEBUG_TASK_PERMISSIONS) {
      console.log('[Tasks Component Permissions Debug]', {
        button: 'وظیفه جدید',
        canAdd,
        tasksApiPermissions: taskPermissions,
        tasksAddLevel: taskPermissions?.add,
        userId: user?.id,
        userRoles: user?.roles?.map((r) => r.name || r),
      });
    }
  }, [canAdd, taskPermissions, user]);

  const [viewMode, setViewMode] = useState(() => initialTasksState.viewMode);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [pinUpdatingId, setPinUpdatingId] = useState(null);
  const [boardColumns, setBoardColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatusModal, setBulkStatusModal] = useState({ open: false, status: undefined });
  const [searchText, setSearchText] = useState(() => initialTasksState.searchText);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState(() => {
    const cached = readCachedAuthUser();
    const employees =
      cached?.id != null && cached?.name
        ? [{ value: toFilterSelectId(cached.id), label: cached.name }]
        : [];
    return {
      projects: [],
      clients: [],
      employees,
      categories: [],
      labels: [],
    };
  });
  const [filterLabels, setFilterLabels] = useState(() => initialTasksState.filterLabels);
  const [assignedToAll, setAssignedToAll] = useState(() => initialTasksState.assignedToAll);
  const [filters, setFilters] = useState(() => initialTasksState.filters);
  const [pagination, setPagination] = useState(() => initialTasksState.pagination);
  const [kanbanCache, setKanbanCache] = useState(null);
  const [kanbanCacheKey, setKanbanCacheKey] = useState('');
  const listCacheRef = useRef({ key: '', tasks: [], total: 0 });

  const buildListCacheKey = useCallback(
    () =>
      JSON.stringify({
        filters,
        searchText,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }),
    [filters, searchText, pagination],
  );

  const invalidateKanbanCache = useCallback(() => {
    setKanbanCache(null);
    setKanbanCacheKey('');
  }, []);

  const handleKanbanCached = useCallback((payload) => {
    setKanbanCache({
      columns: payload.columns,
      tasks: payload.tasks,
      columnHasMore: payload.columnHasMore,
    });
    setKanbanCacheKey(payload.key);
  }, []);

  const applyTaskFilterOptions = useCallback((f) => {
    if (!f || typeof f !== 'object') return;
    setFilterOptions((prev) => ({
      projects: f.projects
        ? mapFilterSelectOptions(f.projects, {
            valueKey: 'id',
            labelKey: 'project_name',
            getLabel: (p) => p.project_name || p.name,
          })
        : prev.projects,
      clients: f.clients
        ? mapFilterSelectOptions(f.clients, { valueKey: 'id', labelKey: 'name' })
        : prev.clients,
      employees: f.employees
        ? mapFilterSelectOptions(f.employees, { valueKey: 'id', labelKey: 'name' })
        : prev.employees,
      categories: f.categories
        ? mapFilterSelectOptions(f.categories, {
            valueKey: 'id',
            labelKey: 'category_name',
            getLabel: (c) => c.category_name || c.name,
          })
        : prev.categories,
      labels: f.labels
        ? mapFilterSelectOptions(f.labels, {
            valueKey: 'id',
            labelKey: 'label_name',
            getLabel: (l) => l.label_name || l.name,
          })
        : prev.labels,
    }));
  }, []);

  const handleFiltersFromBoard = useCallback(
    (f) => {
      if (!f || typeof f !== 'object') return;
      applyTaskFilterOptions(f);
      if (f.taskboard_columns?.length || f.columns?.length || f.boardColumns?.length) {
        setBoardColumns(f.taskboard_columns || f.columns || f.boardColumns || []);
      }
    },
    [applyTaskFilterOptions],
  );

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      viewMode,
      filterLabels,
      assignedToAll,
      kanbanCache,
      kanbanCacheKey,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, searchText, viewMode, filterLabels, assignedToAll, kanbanCache, kanbanCacheKey, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(normalizeTaskFilterIds(restored.filters));
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.viewMode) setViewMode(restored.viewMode);
    if (restored.filterLabels) setFilterLabels(restored.filterLabels);
    if (restored.assignedToAll != null) setAssignedToAll(restored.assignedToAll === true);
    if (restored.kanbanCache) setKanbanCache(restored.kanbanCache);
    if (restored.kanbanCacheKey) setKanbanCacheKey(restored.kanbanCacheKey);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  useEffect(() => {
    if (!user?.id || assignedToAll) return;
    setFilters((prev) => {
      if (prev.assigned_to !== 'all') return prev;
      return { ...prev, assigned_to: toFilterSelectId(user.id) };
    });
    setFilterLabels((prev) =>
      prev.assigned_to || !user.name
        ? prev
        : { ...prev, assigned_to: user.name },
    );
  }, [user?.id, user?.name, assignedToAll]);

  useEffect(() => {
    saveTasksListState({ filters, searchText, viewMode, pagination, filterLabels, assignedToAll });
  }, [filters, searchText, viewMode, pagination, filterLabels, assignedToAll]);

  const resolveEmployeeFilterLabel = useCallback(
    (employeeId) => {
      const id = toFilterSelectId(employeeId);
      if (id === 'all') return null;
      const fromOptions = filterOptions.employees.find((e) => Number(e.value) === Number(id));
      if (fromOptions?.label) return fromOptions.label;
      if (filterLabels.assigned_to && Number(filters.assigned_to) === Number(id)) {
        return filterLabels.assigned_to;
      }
      if (filterLabels.assigned_by && Number(filters.assigned_by) === Number(id)) {
        return filterLabels.assigned_by;
      }
      if (user?.id != null && Number(user.id) === Number(id)) return user.name;
      const cached = readCachedAuthUser();
      if (cached?.id != null && Number(cached.id) === Number(id)) return cached.name;
      return filterLabels[`employee_${id}`] || `کارمند #${id}`;
    },
    [filterOptions.employees, filterLabels, filters.assigned_to, filters.assigned_by, user?.id, user?.name],
  );

  const employeeFilterOptions = useMemo(() => {
    const base = filterOptions.employees || [];
    let options = base;
    if (filters.assigned_to !== 'all') {
      options = ensureFilterSelectOption(
        options,
        filters.assigned_to,
        resolveEmployeeFilterLabel(filters.assigned_to),
      );
    }
    if (filters.assigned_by !== 'all') {
      options = ensureFilterSelectOption(
        options,
        filters.assigned_by,
        resolveEmployeeFilterLabel(filters.assigned_by),
      );
    }
    return options;
  }, [
    filterOptions.employees,
    filters.assigned_to,
    filters.assigned_by,
    resolveEmployeeFilterLabel,
  ]);

  useEffect(() => {
    if (filters.assigned_to === 'all') return;
    const matched = filterOptions.employees.find(
      (e) => Number(e.value) === Number(filters.assigned_to),
    );
    if (matched?.label) {
      setFilterLabels((prev) =>
        prev.assigned_to === matched.label ? prev : { ...prev, assigned_to: matched.label },
      );
      return;
    }
    const cachedName = user?.name || readCachedAuthUser()?.name;
    const cachedId = user?.id ?? readCachedAuthUserId();
    if (cachedName && cachedId != null && Number(filters.assigned_to) === Number(cachedId)) {
      setFilterLabels((prev) =>
        prev.assigned_to ? prev : { ...prev, assigned_to: cachedName },
      );
    }
  }, [filterOptions.employees, filters.assigned_to, user?.id, user?.name]);

  const apiFilters = useMemo(
    () =>
      buildWorkListFilters(
        { ...filters, page: pagination.current, per_page: pagination.pageSize },
        searchText,
        defaultRange,
      ),
    [filters, searchText, pagination.current, pagination.pageSize, defaultRange],
  );

  const kanbanFilters = useMemo(
    () => buildWorkListFilters(filters, searchText, defaultRange),
    [filters, searchText, defaultRange],
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.getAll(apiFilters);
      const block = res?.data?.tasks || res?.tasks;
      const nextTasks = block?.data || [];
      const nextTotal = block?.total ?? 0;
      setTasks(nextTasks);
      setTotal(nextTotal);
      listCacheRef.current = {
        key: buildListCacheKey(),
        tasks: nextTasks,
        total: nextTotal,
      };
      const f = res?.data?.filters || res?.filters;
      if (f) {
        applyTaskFilterOptions(f);
        if (f.taskboard_columns?.length || f.columns?.length || f.boardColumns?.length) {
          setBoardColumns(f.taskboard_columns || f.columns || f.boardColumns || []);
        }
      }
    } catch {
      message.error('خطا در دریافت وظایف');
    } finally {
      setLoading(false);
    }
  }, [apiFilters, applyTaskFilterOptions, buildListCacheKey]);

  useEffect(() => {
    if (viewMode !== 'list') return;
    if (!filters.startDate || !filters.endDate) return;
    const cacheKey = buildListCacheKey();
    if (listCacheRef.current.key === cacheKey) {
      setTasks(listCacheRef.current.tasks);
      setTotal(listCacheRef.current.total);
      return;
    }
    const timer = setTimeout(() => {
      fetchList();
    }, 500);
    return () => clearTimeout(timer);
  }, [viewMode, filters, searchText, pagination.current, pagination.pageSize, buildListCacheKey, fetchList]);

  const handleDelete = (id) => {
    confirmWorkDelete({
      title: 'حذف وظیفه',
      content: 'آیا از حذف این وظیفه اطمینان دارید؟',
      onOk: async () => {
        await tasksApi.delete(id);
        message.success('وظیفه حذف شد');
        fetchList();
      },
    });
  };

  const handleStatusChange = async (row, boardColumnId) => {
    setStatusUpdatingId(row.id);
    try {
      const col = boardColumns.find((c) => String(c.id) === String(boardColumnId));
      const statusValue = col?.slug || boardColumnId;
      await tasksApi.changeStatus(row.id, statusValue);
      message.success('وضعیت به‌روز شد');
      fetchList();
    } catch {
      message.error('خطا در تغییر وضعیت');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handlePin = async (row) => {
    setPinUpdatingId(row.id);
    try {
      const pinRecordId = typeof row.pinned === 'number' || typeof row.pinned === 'string'
        ? row.pinned
        : row.pinned_task?.id || row.pinned?.id;
      if (pinRecordId) {
        await tasksApi.destroyPin(pinRecordId);
        message.success('سنجاق برداشته شد');
      } else {
        const projectId = row.project_id || row.project?.id;
        if (!projectId) {
          message.warning('پروژه وظیفه مشخص نیست');
          return;
        }
        await tasksApi.storePin(row.id, projectId);
        message.success('وظیفه سنجاق شد');
      }
      fetchList();
    } catch {
      message.error('خطا در سنجاق کردن');
    } finally {
      setPinUpdatingId(null);
    }
  };

  const handleDuplicate = (row) => {
    navigate('/work/tasks/add', {
      state: {
        copyFromId: row.id,
        returnTo: '/work/tasks',
      },
    });
  };

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('ابتدا وظایفی را انتخاب کنید');
      return;
    }
    confirmWorkDelete({
      title: 'حذف گروهی',
      content: `آیا از حذف ${selectedRowKeys.length} وظیفه انتخاب‌شده اطمینان دارید؟`,
      onOk: async () => {
        setBulkLoading(true);
        try {
          await tasksApi.applyQuickAction({
            action_type: 'delete',
            row_ids: selectedRowKeys,
          });
          message.success('وظایف حذف شدند');
          setSelectedRowKeys([]);
          fetchList();
        } catch {
          message.error('خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleBulkStatus = async () => {
    if (!selectedRowKeys.length || bulkStatusModal.status == null) {
      message.warning('وضعیت را انتخاب کنید');
      return;
    }
    setBulkLoading(true);
    try {
      await tasksApi.applyQuickAction({
        action_type: 'change-status',
        row_ids: selectedRowKeys,
        status: bulkStatusModal.status,
      });
      message.success('وضعیت وظایف به‌روز شد');
      setBulkStatusModal({ open: false, status: undefined });
      setSelectedRowKeys([]);
      fetchList();
    } catch {
      message.error('خطا در تغییر وضعیت گروهی');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkActionMenuItems = [
    {
      key: 'change-status',
      label: <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت</span>,
      icon: <EditOutlined />,
      onClick: () => setBulkStatusModal({ open: true, status: undefined }),
    },
    {
      key: 'delete',
      label: <span style={{ fontFamily: 'CustomFont' }}>حذف</span>,
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await tasksApi.exportList(buildWorkListFilters(filters, searchText, defaultRange));
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const selectFilterKeys = new Set([
    'project_id',
    'client_id',
    'assigned_to',
    'assigned_by',
    'category_id',
    'label_id',
  ]);

  const handleFilterChange = (key, val) => {
    if (key === 'date_range') {
      const next = applyWorkDateRangeChange(val, defaultRange);
      if (!next) return;
      setFilters((p) => ({ ...p, ...next }));
      setPagination((p) => ({ ...p, current: 1 }));
      invalidateKanbanCache();
      return;
    }

    const nextValue =
      val == null || val === ''
        ? 'all'
        : selectFilterKeys.has(key)
          ? toFilterSelectId(val)
          : val;

    setFilters((p) => ({ ...p, [key]: nextValue }));
    setPagination((p) => ({ ...p, current: 1 }));
    invalidateKanbanCache();

    if (key === 'assigned_to') {
      setAssignedToAll(nextValue === 'all');
    }

    if (selectFilterKeys.has(key) && nextValue !== 'all') {
      const optionsSource =
        key === 'assigned_to' || key === 'assigned_by'
          ? employeeFilterOptions
          : key === 'project_id'
            ? filterOptions.projects
            : key === 'client_id'
              ? filterOptions.clients
              : key === 'category_id'
                ? filterOptions.categories
                : filterOptions.labels;
      const matched = (optionsSource || []).find(
        (o) => Number(o.value) === Number(nextValue),
      );
      if (matched?.label) {
        setFilterLabels((prev) => ({ ...prev, [key]: matched.label }));
      }
    } else if (selectFilterKeys.has(key)) {
      setFilterLabels((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const quickFilters = [
    {
      key: 'datePreset',
      type: 'datePreset',
      label: 'بازه سریع',
      placeholder: 'بازه سریع',
      options: buildCommonDatePresets(),
      onPreset: (range) => {
        setFilters((prev) => ({
          ...prev,
          startDate: range.startDate,
          endDate: range.endDate,
        }));
        setPagination((p) => ({ ...p, current: 1 }));
        invalidateKanbanCache();
      },
    },
    createWorkDateRangeFilter(filters.startDate, filters.endDate),
    {
      key: 'assigned_to',
      type: 'select',
      label: 'مسئول',
      placeholder: 'مسئول',
      value: filters.assigned_to === 'all' ? undefined : filters.assigned_to,
      options: employeeFilterOptions,
      showSearch: true,
    },
    {
      key: 'label_id',
      type: 'select',
      label: 'تگ',
      value: filters.label_id === 'all' ? undefined : filters.label_id,
      options: ensureFilterSelectOption(
        filterOptions.labels,
        filters.label_id,
        filterLabels.label_id,
      ),
    },
  ];

  const advancedFilters = [
    {
      key: 'project_id',
      type: 'select',
      label: 'پروژه',
      value: filters.project_id === 'all' ? undefined : filters.project_id,
      options: ensureFilterSelectOption(
        filterOptions.projects,
        filters.project_id,
        filterLabels.project_id,
      ),
      showSearch: true,
    },
    {
      key: 'client_id',
      type: 'select',
      label: 'مشتری',
      value: filters.client_id === 'all' ? undefined : filters.client_id,
      options: ensureFilterSelectOption(
        filterOptions.clients,
        filters.client_id,
        filterLabels.client_id,
      ),
      showSearch: true,
    },
    {
      key: 'assigned_by',
      type: 'select',
      label: 'ایجادکننده',
      value: filters.assigned_by === 'all' ? undefined : filters.assigned_by,
      options: employeeFilterOptions,
      showSearch: true,
    },
    {
      key: 'priority',
      type: 'select',
      label: 'اولویت',
      value: filters.priority === 'all' ? undefined : filters.priority,
      options: [
        { value: 'high', label: 'بالا' },
        { value: 'medium', label: 'متوسط' },
        { value: 'low', label: 'پایین' },
      ],
    },
    {
      key: 'category_id',
      type: 'select',
      label: 'دسته',
      value: filters.category_id === 'all' ? undefined : filters.category_id,
      options: ensureFilterSelectOption(
        filterOptions.categories,
        filters.category_id,
        filterLabels.category_id,
      ),
    },
  ];

  return (
    <WorkListPage
      title="مدیریت وظایف"
      subtitle="مشاهده و پیگیری وظایف"
      icon={CheckSquareOutlined}
      headerExtra={(
        <Space wrap>
          <Button
            icon={<FileExcelOutlined />}
            loading={exporting}
            onClick={handleExportExcel}
            style={{ fontFamily: 'CustomFont' }}
          >
            خروجی اکسل
          </Button>
          <Button
            icon={<AppstoreAddOutlined />}
            onClick={() => navigate('/work/tasks/board-settings')}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن ستون وضعیت
          </Button>
          <PermissionGate permission="add_task" debug={DEBUG_TASK_PERMISSIONS}>
            {canAdd ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                onClick={() => navigate('/work/tasks/add')}
                style={{ fontFamily: 'CustomFont' }}
              >
                وظیفه جدید
              </Button>
            ) : null}
          </PermissionGate>
        </Space>
      )}
    >
            <div className="mb-6 flex items-center justify-end">
              <Segmented
                value={viewMode}
                onChange={setViewMode}
                options={[
                  {
                    label: (
                      <div className="flex items-center gap-2 px-2">
                        <UnorderedListOutlined />
                        <span style={{ fontFamily: 'CustomFont' }}>لیست</span>
                      </div>
                    ),
                    value: 'list',
                  },
                  {
                    label: (
                      <div className="flex items-center gap-2 px-2">
                        <AppstoreOutlined />
                        <span style={{ fontFamily: 'CustomFont' }}>کانبان</span>
                      </div>
                    ),
                    value: 'kanban',
                  },
                ]}
                style={{ fontFamily: 'CustomFont' }}
              />
            </div>

            <div className="mb-6">
              <StandardFilterPanel
                searchValue={searchText}
                onSearchChange={(value) => {
                  setSearchText(value);
                  setPagination((p) => ({ ...p, current: 1 }));
                  invalidateKanbanCache();
                }}
                onSearch={() => {}}
                searchPlaceholder="جستجو عنوان وظیفه..."
                quickFilters={quickFilters}
                advancedFilters={advancedFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={() => {
                  const uid = user?.id ?? readCachedAuthUserId();
                  setAssignedToAll(false);
                  setFilters(buildDefaultTaskFilters(uid, defaultRange));
                  const cachedName = user?.name || readCachedAuthUser()?.name;
                  setFilterLabels(cachedName ? { assigned_to: cachedName } : {});
                  setSearchText('');
                  setPagination((p) => ({ ...p, current: 1 }));
                  invalidateKanbanCache();
                }}
                totalCount={viewMode === 'list' ? total : undefined}
                showAdvancedFilters
                showTotalCount={viewMode === 'list'}
              />
            </div>

            {viewMode === 'list' && selectedRowKeys.length > 0 ? (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkLoading}>
                  <Button
                    type="primary"
                    loading={bulkLoading}
                    icon={<DownOutlined />}
                    className={PANEL_PRIMARY_BTN_CLASS}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
                <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ fontFamily: 'CustomFont' }}>
                  لغو انتخاب
                </Button>
              </div>
            ) : null}

            {viewMode === 'kanban' ? (
              <TaskKanbanBoard
                active={viewMode === 'kanban'}
                filters={kanbanFilters}
                cachedBoard={kanbanCache}
                cachedBoardKey={kanbanCacheKey}
                onBoardCached={handleKanbanCached}
                onFiltersFromResponse={handleFiltersFromBoard}
                onTaskClick={(task) =>
                  navigateToDetail(`/work/tasks/${task.id}`, {}, {
                    listPath: '/work/tasks',
                    listLabel: 'مدیریت وظایف',
                    entityLabel: task.heading || `وظیفه #${task.id}`,
                  })
                }
              />
            ) : loading ? (
              <TableSkeleton rows={6} columns={5} />
            ) : (
              <TasksTable
                data={tasks}
                loading={false}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) =>
                    setPagination({ current: page, pageSize }),
                }}
                onView={(id, row) =>
                  navigateToDetail(`/work/tasks/${id}`, {}, {
                    listPath: '/work/tasks',
                    listLabel: 'مدیریت وظایف',
                    entityLabel: row?.heading || `وظیفه #${id}`,
                  })
                }
                onEdit={(id) =>
                  navigateToDetail(`/work/tasks/${id}/edit`, {}, {
                    listPath: '/work/tasks',
                    listLabel: 'مدیریت وظایف',
                    entityLabel: `ویرایش وظیفه #${id}`,
                  })
                }
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onPin={handlePin}
                onStatusChange={handleStatusChange}
                statusOptions={boardColumns}
                statusUpdatingId={statusUpdatingId}
                pinUpdatingId={pinUpdatingId}
                canEditRecord={canEditRecord}
                canDeleteRecord={canDeleteRecord}
              />
            )}

      <Modal
        open={bulkStatusModal.open}
        title={<span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت گروهی</span>}
        onCancel={() => setBulkStatusModal({ open: false, status: undefined })}
        onOk={handleBulkStatus}
        confirmLoading={bulkLoading}
        okText="اعمال"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <div style={{ fontFamily: 'CustomFont' }}>
          <p className="mb-2">وضعیت جدید برای {selectedRowKeys.length} وظیفه:</p>
          <Select
            className="w-full"
            placeholder="انتخاب وضعیت"
            value={bulkStatusModal.status}
            onChange={(status) => setBulkStatusModal((p) => ({ ...p, status }))}
            options={boardColumns.map((c) => ({
              value: c.id,
              label: c.column_name || c.name,
            }))}
          />
        </div>
      </Modal>
    </WorkListPage>
  );
};

export default Tasks;
